import { useEffect, useState, type ReactNode } from 'react'

export interface Tab {
  id: string
  /** Ya traducido: quien arma las pestañas sabe en qué idioma está. */
  label: string
  content: ReactNode
  /**
   * Lo que espera adentro, visible sin entrar: en el Centro de Mando, un pedido de apoyo no puede
   * quedar escondido detrás de otra pestaña. Crítico se pinta en rojo; cero no se muestra.
   */
  badge?: { count: number; critical?: boolean; label?: string }
}

/**
 * Pestañas para un área con varios paneles.
 *
 * Un área que crece se vuelve una columna larguísima donde hay que bajar buscando el panel que
 * uno quería. Las pestañas agrupan por **etapa del trabajo**, no por pantalla del backend: el
 * analista primero mira qué datos tiene, después los explora, y al final calcula y publica.
 *
 * La elegida se recuerda en el navegador, por área: quien entra a revisar calidad de datos
 * tres veces al día no tiene que volver a buscarla cada vez.
 */
/** Pide a las pestañas de un área que abran una: `{ area, tab }`. La usa la guía de «qué le toca». */
export const TAB_EVENT = 'protect:tab'

export function Tabs({ area, tabs, active: controlada, onChange }: {
  area: string
  tabs: Tab[]
  /** Quien la maneja desde fuera (el mapa lleva a la pestaña de lo que se eligió en él). */
  active?: string
  onChange?: (id: string) => void
}) {
  const clave = `tab:${area}`
  const [interna, setInterna] = useState(() => {
    try {
      const guardada = localStorage.getItem(clave)
      if (guardada && tabs.some((t) => t.id === guardada)) return guardada
    } catch {
      // Ventana privada o almacenamiento bloqueado: se abre en la primera y no pasa nada.
    }
    return tabs[0]?.id ?? ''
  })

  // Si la pestaña recordada deja de existir —porque el rol de quien entra no la abre— se cae a la
  // primera disponible en vez de mostrar un panel vacío.
  useEffect(() => {
    const abrir = (e: Event) => {
      const { area: a, tab } = (e as CustomEvent<{ area: string; tab: string }>).detail
      if (a === area && tabs.some((t) => t.id === tab)) { setInterna(tab); onChange?.(tab) }
    }
    window.addEventListener(TAB_EVENT, abrir)
    return () => window.removeEventListener(TAB_EVENT, abrir)
  }, [area, tabs, onChange])

  const active = controlada ?? interna
  const setActive = (id: string) => { setInterna(id); onChange?.(id) }
  const elegida = tabs.some((t) => t.id === active) ? active : tabs[0]?.id ?? ''

  useEffect(() => {
    try {
      localStorage.setItem(clave, elegida)
    } catch {
      // Igual que arriba: recordar es una comodidad, no un requisito.
    }
  }, [clave, elegida])

  if (tabs.length === 0) return null

  const mover = (paso: number) => {
    const i = tabs.findIndex((t) => t.id === elegida)
    const siguiente = tabs[(i + paso + tabs.length) % tabs.length]
    if (siguiente) setActive(siguiente.id)
  }

  return (
    <>
      <div className="tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            id={`tab-${area}-${t.id}`}
            aria-selected={t.id === elegida}
            aria-controls={`panel-${area}-${t.id}`}
            tabIndex={t.id === elegida ? 0 : -1}
            className={t.id === elegida ? 'tab active' : 'tab'}
            onClick={() => setActive(t.id)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') mover(1)
              if (e.key === 'ArrowLeft') mover(-1)
            }}
          >
            {t.label}
            {t.badge && t.badge.count > 0 && (
              <span
                className={t.badge.critical ? 'tab-badge critical' : 'tab-badge'}
                aria-label={t.badge.label}
                title={t.badge.label}
              >
                {t.badge.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tabs.map((t) => (
        t.id === elegida && (
          <div
            key={t.id}
            role="tabpanel"
            id={`panel-${area}-${t.id}`}
            aria-labelledby={`tab-${area}-${t.id}`}
          >
            {t.content}
          </div>
        )
      ))}
    </>
  )
}
